import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { fetchPublicConsent, signPublicConsent } from 'api'

import SignaturePad from '../Operations/components/SignaturePad'

/**
 * THE PARENT'S PAGE. No login, no Alfred account — a parent must not need one to
 * give consent for their own child. The token in the URL is the authorisation.
 *
 * Laid out like the Openhouse "Underage Consent" Google Form the parents already
 * know: one question per card, top to bottom, on a phone. The form is raised at
 * PRE-REGISTRATION, so Alfred usually knows nothing about the child yet — this
 * page is where the child's details are captured for the first time, and every
 * question is required for that reason. Anything Alfred does hold is pre-filled
 * and stays editable. The one addition the Google Form can't do is a real
 * signature at the end, which is what makes this an undertaking, not a survey.
 */
const CENTRES = [
  'HSR',
  'Haralur',
  'Sarjapur',
  'Indiranagar',
  'Whitefield',
  'HRBR',
  'Sadashivnagar',
  'Sahakarnagar',
  'JP Nagar',
  'Jayanagar'
]

// The single "Grade Requested" question, exactly as the Google Form words it.
const GRADES = [
  'Toddler (Ages 1-2)',
  'Pre-Nursery (Ages 2-3)',
  'Nursery (Ages 3-4)',
  'K1 (Ages 4-5)',
  'K2 (Ages 5-6)'
]

const ACKNOWLEDGEMENT =
  'I take full responsibility for this decision and any impacts it may have on ' +
  'my child’s progress. I agree that my child will repeat a grade before ' +
  'moving on to Kindergarten 1 to meet the age requirements.'

// Alfred stores a programme name ("class 1", "ukg", "todlr (age 1)"…). Map it to
// the Google Form's five buckets so the right radio is pre-selected where we can;
// no confident match just leaves it for the parent to pick.
const guessGrade = (program) => {
  const p = (program || '').toLowerCase()
  if (!p) return ''
  if (p.includes('toddler') || p.includes('todlr')) return GRADES[0]
  if (p.includes('pre')) return GRADES[1]
  if (p.includes('nursery') || p.includes('nur')) return GRADES[2]
  if (p.includes('k2') || p.includes('ukg') || p.includes('kg2')) return GRADES[4]
  if (p.includes('k1') || p.includes('lkg') || p.includes('kg1')) return GRADES[3]
  return ''
}

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: '100vh',
    background: '#f3efe8',
    padding: theme.spacing(2, 1.5, 6),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  stack: { width: '100%', maxWidth: 620 },
  card: {
    background: '#fff',
    borderRadius: 8,
    border: '1px solid #e3ddd3',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(1.75)
  },
  headerCard: {
    background: '#fff',
    borderRadius: 8,
    borderTop: '10px solid #f4511e',
    border: '1px solid #e3ddd3',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    padding: theme.spacing(3, 3, 2.5),
    marginBottom: theme.spacing(1.75)
  },
  brand: { color: '#f4511e', fontWeight: 700, letterSpacing: 0.3 },
  title: { marginTop: theme.spacing(0.5), fontWeight: 600 },
  desc: { marginTop: theme.spacing(1.5), color: '#5f5a52', lineHeight: 1.5 },
  prefillNote: {
    marginTop: theme.spacing(2),
    background: '#fff3e0',
    color: '#8a4b00',
    borderRadius: 8,
    padding: theme.spacing(1, 1.5),
    fontSize: 13,
    lineHeight: 1.5
  },
  question: {
    fontSize: 15,
    fontWeight: 600,
    color: '#26211b',
    marginBottom: theme.spacing(1.5)
  },
  req: { color: '#d93025', marginLeft: 2 },
  input: { maxWidth: 360 },
  ack: { alignItems: 'flex-start', marginRight: 0 },
  ackText: { fontSize: 14, color: '#26211b', lineHeight: 1.5 },
  sigLabel: {
    fontSize: 15,
    fontWeight: 600,
    color: '#26211b',
    marginBottom: theme.spacing(1)
  },
  submitRow: { marginTop: theme.spacing(0.5) },
  submitBtn: {
    background: '#f4511e',
    color: '#fff',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    '&:hover': { background: '#d84315' },
    '&:disabled': { background: '#e0d8cd', color: '#fff' }
  },
  centered: { textAlign: 'center', padding: theme.spacing(6) }
}))

const UnderageConsentPublic = () => {
  const classes = useStyles()
  const { token } = useParams()

  const [row, setRow] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)

  const [parentName, setParentName] = useState('')
  const [parentPhone, setParentPhone] = useState('')
  const [signature, setSignature] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)

  // The factual cells, seeded from Alfred, every one editable.
  const [fill, setFill] = useState({
    centre_name: '',
    child_name: '',
    child_dob: '',
    grade_requested: '',
    required_age_years: '',
    days_underage: '',
    academic_year: ''
  })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchPublicConsent(token)
      setRow(data)
      setParentName(data?.parent_name || '')
      setParentPhone(data?.parent_phone || '')
      setFill({
        centre_name: data?.centre_name || '',
        child_name: data?.child_name || '',
        child_dob: data?.child_dob || '',
        grade_requested: guessGrade(data?.program),
        required_age_years:
          data?.required_age_years === null ||
          data?.required_age_years === undefined
            ? ''
            : String(data.required_age_years),
        days_underage:
          data?.days_underage === null || data?.days_underage === undefined
            ? ''
            : String(data.days_underage),
        academic_year: data?.academic_year || ''
      })
      if (data?.status === 'signed') setDone(true)
    } catch (e) {
      // A bad token and an unknown token look the same on purpose.
      setError('This link is not valid. Please ask the centre for a new one.')
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    load()
  }, [load])

  // Capture the value NOW — React 16 pools the SyntheticEvent and nulls its
  // target once the handler returns, so a lazy read inside the functional updater
  // blows up ("Cannot read properties of null") and white-screens the page.
  const setCell = (key) => (e) => {
    const { value } = e.target
    setFill((f) => ({ ...f, [key]: value }))
  }

  // EVERY question is required. The form is raised before the child is
  // registered, so these answers are not a correction of what Alfred holds —
  // they are the only record there is, and a half-filled undertaking is not one.
  const canSubmit =
    !!parentName.trim() &&
    !!parentPhone.trim() &&
    !!fill.centre_name &&
    !!fill.child_name.trim() &&
    !!fill.child_dob &&
    !!fill.grade_requested &&
    !!String(fill.required_age_years).trim() &&
    !!String(fill.days_underage).trim() &&
    acknowledged &&
    !!signature

  const submit = async () => {
    if (!canSubmit) return
    setSaving(true)
    try {
      await signPublicConsent(token, {
        parent_name: parentName.trim(),
        parent_phone: parentPhone.trim(),
        parent_signature: signature,
        acknowledged: true,
        centre_name: fill.centre_name,
        child_name: fill.child_name.trim(),
        child_dob: fill.child_dob,
        // one "Grade Requested" answer feeds both the programme and grade cells
        program: fill.grade_requested,
        grade: fill.grade_requested,
        required_age_years: String(fill.required_age_years).trim(),
        days_underage: String(fill.days_underage).trim(),
        academic_year: fill.academic_year
      })
      setDone(true)
    } catch (e) {
      setError(
        e?.response?.status === 409
          ? 'This form has already been signed. Please contact the centre.'
          : 'Could not submit. Please try again.'
      )
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box className={classes.page}>
        <Box className={classes.centered}>
          <CircularProgress />
        </Box>
      </Box>
    )
  }

  if (error && !row) {
    return (
      <Box className={classes.page}>
        <Box className={classes.stack}>
          <Box className={classes.card}>
            <Typography variant="h6">{error}</Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  if (done) {
    return (
      <Box className={classes.page}>
        <Box className={classes.stack}>
          <Box className={classes.headerCard}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <CheckCircleIcon style={{ color: '#2e7d32' }} />
              <Typography variant="h6">Thank you — this is signed.</Typography>
            </Box>
            <Typography className={classes.desc}>
              The centre has your consent for {fill.child_name || 'your child'}.
              You can close this page. If anything needs changing, please contact
              the centre.
            </Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  const Req = () => <span className={classes.req}>*</span>

  return (
    <Box className={classes.page}>
      <Box className={classes.stack}>
        {/* Header — matches the Google Form's intro */}
        <Box className={classes.headerCard}>
          <Typography variant="body2" className={classes.brand}>
            openhouse early years
          </Typography>
          <Typography variant="h5" className={classes.title}>
            Underage Consent Form &amp; Parent Undertaking
          </Typography>
          <Typography className={classes.desc}>
            Dear Parent, if your child doesn&apos;t yet meet the minimum age for
            their grade, please fill out this form to give your consent for
            enrollment. — Team Openhouse
          </Typography>
          <Typography className={classes.prefillNote}>
            Every question needs an answer. Where we already have something on
            record we&apos;ve filled it in for you — please check it and correct
            anything that&apos;s wrong.
          </Typography>
        </Box>

        {/* Centre Name */}
        <Box className={classes.card}>
          <Typography className={classes.question}>
            Centre Name
            <Req />
          </Typography>
          <TextField
            select
            fullWidth
            className={classes.input}
            value={fill.centre_name}
            onChange={setCell('centre_name')}
          >
            {(CENTRES.includes(fill.centre_name) || !fill.centre_name
              ? CENTRES
              : [fill.centre_name, ...CENTRES]
            ).map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Parent Name */}
        <Box className={classes.card}>
          <Typography className={classes.question}>
            Parent Name
            <Req />
          </Typography>
          <TextField
            fullWidth
            className={classes.input}
            placeholder="Your answer"
            value={parentName}
            onChange={(e) => setParentName(e.target.value)}
          />
        </Box>

        {/* Openhouse Registered Parent Number */}
        <Box className={classes.card}>
          <Typography className={classes.question}>
            Openhouse Registered Parent Number
            <Req />
          </Typography>
          <TextField
            fullWidth
            className={classes.input}
            placeholder="Your answer"
            value={parentPhone}
            onChange={(e) => setParentPhone(e.target.value)}
          />
        </Box>

        {/* Child Name */}
        <Box className={classes.card}>
          <Typography className={classes.question}>
            Child Name
            <Req />
          </Typography>
          <TextField
            fullWidth
            className={classes.input}
            placeholder="Your answer"
            value={fill.child_name}
            onChange={setCell('child_name')}
          />
        </Box>

        {/* Child Date of Birth */}
        <Box className={classes.card}>
          <Typography className={classes.question}>
            Child Date of Birth
            <Req />
          </Typography>
          <TextField
            type="date"
            className={classes.input}
            InputLabelProps={{ shrink: true }}
            value={fill.child_dob}
            onChange={setCell('child_dob')}
          />
        </Box>

        {/* Grade Requested for Enrollment */}
        <Box className={classes.card}>
          <Typography className={classes.question}>
            Grade Requested for Enrollment
            <Req />
          </Typography>
          <RadioGroup
            value={fill.grade_requested}
            onChange={setCell('grade_requested')}
          >
            {GRADES.map((g) => (
              <FormControlLabel
                key={g}
                value={g}
                control={<Radio style={{ color: '#f4511e' }} />}
                label={g}
              />
            ))}
          </RadioGroup>
        </Box>

        {/* Required age for the program */}
        <Box className={classes.card}>
          <Typography className={classes.question}>
            Required age for the program (per Openhouse Age Policy)
            <Req />
          </Typography>
          <TextField
            fullWidth
            className={classes.input}
            placeholder="Your answer"
            value={fill.required_age_years}
            onChange={setCell('required_age_years')}
          />
        </Box>

        {/* Number of days underage */}
        <Box className={classes.card}>
          <Typography className={classes.question}>
            Number of days the child is underage for the requested grade
            <Req />
          </Typography>
          <TextField
            fullWidth
            className={classes.input}
            placeholder="Your answer"
            value={fill.days_underage}
            onChange={setCell('days_underage')}
          />
        </Box>

        {/* Acknowledgment */}
        <Box className={classes.card}>
          <Typography className={classes.question}>
            Acknowledgment Statement
            <Req />
          </Typography>
          <FormControlLabel
            className={classes.ack}
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                style={{ color: '#f4511e', paddingTop: 0 }}
              />
            }
            label={<span className={classes.ackText}>{ACKNOWLEDGEMENT}</span>}
          />
        </Box>

        {/* Signature — the one thing the Google Form can't capture */}
        <Box className={classes.card}>
          <Typography className={classes.sigLabel}>
            Parent / guardian signature
            <Req />
          </Typography>
          <SignaturePad
            label=""
            value={signature}
            onChange={setSignature}
          />
        </Box>

        {error ? (
          <Typography
            variant="body2"
            color="error"
            style={{ marginBottom: 12 }}
          >
            {error}
          </Typography>
        ) : null}

        <Box className={classes.submitRow}>
          <Button
            className={classes.submitBtn}
            variant="contained"
            disableElevation
            size="large"
            disabled={saving || !canSubmit}
            onClick={submit}
          >
            {saving ? 'submitting…' : 'Submit'}
          </Button>
          {!canSubmit ? (
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              style={{ marginTop: 8 }}
            >
              Please answer every required question, tick the acknowledgment and
              sign.
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  )
}

export default UnderageConsentPublic
